<script setup>
import { ref, computed } from "vue";
import { storeToRefs } from "pinia";
import { useCartStore } from "@/stores/CartStore";
import { useTrackStore } from "@/stores/TrackStore";
import { licenses } from "@/data/licenses";

const cartStore = useCartStore();
const trackStore = useTrackStore();
const { showingCart, tracks, count, isEmpty } = storeToRefs(cartStore);

const selectTrack = function selectTrack(track) {
  trackStore.selectTrack(track);
};

const questions = ref([
  {
    choices: [
      {
        next: "assistant",
        label: "Help me choose",
        answer: "Help me choose",
      },
      {
        next: "license",
        label: "Just let me select",
        answer: "Just let me select",
      },
    ],
    answer: null,
  },
]);

const showLicense = ref(false);

const distributionChoices = [
  { name: "internal", label: "Personal or internal use" },
  { name: "social", label: "Social media, live streaming" },
  { name: "public", label: "Exhibition, public show, theatrical" },
  { name: "private", label: "Music on hold, private communication" },
  { name: "training", label: "Online course, e-learning" },
  { name: "software", label: "Software, video game" },
  { name: "reproduction", label: "CD, DVD, reproduction" },
  { name: "podcast", label: "Audio book, podcast" },
  { name: "online", label: "Online advertising" },
  { name: "global", label: "Global media, worldwide ads" },
  { name: "remix", label: "Remix, reselling" },
];

let selectedDistributions = ref([]);

const requiredFeatures = computed(() => {
  let requiredFeatures = [];
  for (let question of questions.value) {
    if (question.answer) {
      if (question.answer.requiredFeatures) {
        requiredFeatures = requiredFeatures.concat(
          question.answer.requiredFeatures
        );
      }
    }
  }
  return requiredFeatures;
});

const requiredLicense = computed(() => {
  if (!showLicense.value) return null;

  let requiredLicense = null;
  for (let license of licenses) {
    requiredLicense = license;
    if (
      requiredFeatures.value.every((feature) =>
        license.providedFeatures.includes(feature)
      )
    ) {
      break;
    }
  }
  return requiredLicense;
});

const nextQuestions = {
  assistant: {
    title: "You are a:",
    choices: [
      { next: "business", label: "Business", answer: "Business" },
      { next: "individual", label: "Individual", answer: "Individual" },
    ],
    answer: null,
  },
  audience: {
    title: "What is the size of the target audience?",
    choices: [
      {
        label: "Up to 10K",
        answer: "Audience up to 10K",
        requiredFeatures: ["audience_10k"],
      },
      {
        label: "Up to 250K",
        answer: "Audience up to 250K",
        requiredFeatures: ["audience_250k"],
      },
      {
        label: "Up to 1 million",
        answer: "Audience up to 1 million",
        requiredFeatures: ["audience_1m"],
      },
      {
        label: "Over 1 million",
        answer: "Audience over 1 million",
        requiredFeatures: ["audience_unlimited"],
      },
    ],
    answer: null,
  },
  business: {
    title: "What describes your business the best?",
    choices: [
      { next: "client_size_biz", label: "Agency", answer: "Agency" },
      {
        next: "client_size_biz",
        label: "Production Company",
        answer: "Production Company",
      },
      { next: "business_size", label: "Brand", answer: "Brand" },
      { next: "business_size", label: "Non-Profit", answer: "Non-Profit" },
    ],
    answer: null,
  },
  business_size: {
    title: "How large is your organization?",
    choices: [
      {
        next: "distribution",
        label: "Micro (1-10 employees)",
        answer: "Micro (1-10 employees)",
        requiredFeatures: ["team_10"],
      },
      {
        next: "distribution",
        label: "Small (11-50 employees)",
        answer: "Small (11-50 employees)",
        requiredFeatures: ["team_50"],
      },
      {
        next: "distribution",
        label: "Medium (51-250 employes)",
        answer: "Medium (51-250 employes)",
        requiredFeatures: ["team_250"],
      },
      {
        license: "distribution",
        label: "Large (250+ employees)",
        answer: "Large (250+ employees)",
        requiredFeatures: ["team_unlimited"],
      },
    ],
    answer: null,
  },
  client_size_biz: {
    title: "Select the size of your client(s):",
    choices: [
      {
        next: "distribution",
        label: "Micro (1-10 employees)",
        answer: "Micro client (1-10 employees)",
        requiredFeatures: ["team_10"],
      },
      {
        next: "distribution",
        label: "Small (11-50 employees)",
        answer: "Small client (11-50 employees)",
        requiredFeatures: ["team_50"],
      },
      {
        next: "distribution",
        label: "Medium (51-250 employes)",
        answer: "Medium client (51-250 employes)",
        requiredFeatures: ["team_250"],
      },
      {
        next: "distribution",
        label: "Large (250+ employees)",
        answer: "Large client (250+ employees)",
        requiredFeatures: ["team_unlimited"],
      },
    ],
    answer: null,
  },
  client_size_indie: {
    title: "Select the size of your client(s):",
    choices: [
      {
        label: "Micro (1-10 employees)",
        answer: "Micro client (1-10 employees)",
        requiredFeatures: ["team_10"],
      },
      {
        label: "Small (11-50 employees)",
        answer: "Small client (11-50 employees)",
        requiredFeatures: ["team_50"],
      },
      {
        label: "Medium (51-250 employes)",
        answer: "Medium client (51-250 employes)",
        requiredFeatures: ["team_250"],
      },
      {
        label: "Large (250+ employees)",
        answer: "Large client (250+ employees)",
        requiredFeatures: ["team_unlimited"],
      },
    ],
    answer: null,
  },
  client_work: {
    title: "Is this for client work?",
    choices: [
      { next: "client_size_indie", label: "Yes", answer: "Client work" },
      {
        label: "No",
        answer: "Not client work",
        requiredFeatures: ["team_10"],
      },
    ],
    answer: null,
  },
  distribution: {
    title: "How will you use the song(s)?",
    multiple_choices: distributionChoices,
  },
  individual: {
    title: "What describes you the best?",
    choices: [
      {
        next: "subscribers",
        label: "YouTube Creator",
        answer: "YouTube Creator",
        requiredFeatures: ["distribution_social"],
        include: { answer: { answer: "Web/Social", included: true } },
      },
      {
        next: "subscribers",
        label: "Twitch Streamer",
        answer: "Twitch Streamer",
        requiredFeatures: ["distribution_social"],
        include: { answer: { answer: "Web/Social", included: true } },
      },
      {
        next: "client_size_biz",
        label: "Freelance Filmmaker",
        answer: "Freelance Filmmaker",
      },
      {
        next: "audience",
        label: "Podcaster",
        answer: "Podcaster",
        requiredFeatures: ["distribution_podcast"],
        include: { answer: { answer: "Podcast", included: true } },
      },
      {
        label: "Wedding Filmmaker",
        answer: "Wedding Filmmaker",
        requiredFeatures: ["team_10", "distribution_social", "audience_10k"],
        include: { answer: { answer: "Web/Social", included: true } },
      },
      {
        next: "subscribers",
        label: "Influencer",
        answer: "Influencer",
        requiredFeatures: ["distribution_social"],
        include: { answer: { answer: "Web/Social", included: true } },
      },
      {
        label: "Non-profit User",
        answer: "Non-profit User",
        requiredFeatures: ["distribution_internal"],
        include: { answer: { answer: "Personal use", included: true } },
      },
    ],
    answer: null,
  },
  license: {
    title: "Select your license",
    choices: licenses.map((license) => ({
      label: license.name,
      answer: license.name,
      requiredFeatures: `license_${license.key}`,
    })),
    answer: null,
  },
  subscribers: {
    title: "How many subscribers do you have?",
    choices: [
      {
        next: "client_work",
        label: "Up to 10K",
        answer: "Up to 10K subscribers",
        requiredFeatures: ["audience_10k"],
      },
      {
        next: "client_work",
        label: "Up to 250K",
        answer: "Up to 250K subscribers",
        requiredFeatures: ["audience_250k"],
      },
      {
        next: "client_work",
        label: "Up to 1 million",
        answer: "Up to 1 million subscribers",
        requiredFeatures: ["audience_1m"],
      },
      {
        next: "client_work",
        label: "Over 1 million",
        answer: "Over 1 million subscribers",
        requiredFeatures: ["audience_unlimited"],
      },
    ],
    answer: null,
  },
};

const selectAnswer = function selectAnswer(question, answer) {
  question.answer = answer;
  if (answer.include) {
    questions.value.push(answer.include);
  }
  const nextQuestion = nextQuestions[answer.next];
  if (nextQuestion) {
    nextQuestion.answer = null;
    questions.value.push(nextQuestion);
  } else {
    showLicense.value = true;
  }
};

const selectDistribution = function selectDistribution(question) {
  question.answer = {
    answer: selectedDistributions.value
      .map((d) => distributionChoices.find((c) => c.name === d).label)
      .join(", "),
    requiredFeatures: selectedDistributions.value.map(
      (d) => `distribution_${d}`
    ),
  };
  // license.value = "contact_us"; // TODO: change to "standard"
  const nextQuestion = nextQuestions["audience"];
  nextQuestion.answer = null;
  questions.value.push(nextQuestion);
};

function clearAnswer(question) {
  showLicense.value = false;
  question.answer = null;
  questions.value = questions.value.slice(
    0,
    questions.value.indexOf(question) + 1
  );
}

const mailto = computed(() => {
  const recipient = "licensing@odolib.com";
  const subject = "License Inquiry";
  const songs = Array.from(tracks.value.values())
    .map((t, index) => `${index + 1}. ${t.title} - ${t.artistName}`)
    .join("\n");
  const answers = questions.value
    .map((q) => {
      if (q.multiple_choices) {
        return `- ${q.title} ${q.answer.answer}`;
      }
      return `- ${q.title} ${q.answer.label}`;
    })
    .join("\n");
  const body = `
Full name:
Business email:
End client name:
Additional details:

I would like to inquire about a license for the following song${
    count.value > 1 ? "s" : ""
  }:

${songs}

----- Please do not modify below -----

${answers}

- needs: ${requiredFeatures.value.join(", ")}

==> ${requiredLicense.value.name} LICENSE

`.trim();
  return `mailto:${recipient}?subject=${subject}&body=${encodeURIComponent(
    body
  )}`;
});
</script>

<style scoped>
.cover-container {
  position: relative;
  display: inline-block;
}

.cover-container img {
  transition: transform 0.2s ease-in-out, filter 0.2s ease-in-out,
    box-shadow 0.2s ease-in-out;
}

.cover-container:hover img {
  transform: scale(1.1);
  filter: brightness(1.2) saturate(1.2);
  box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.3);
  cursor: pointer;
}

.play-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;
  pointer-events: none;
}

.cover-container:hover .play-icon {
  visibility: visible;
  opacity: 1;
}

.play-icon circle {
  fill: rgba(0, 0, 0, 0.6);
}

.play-icon polygon {
  /* fill: #fff; */
}
</style>

<template>
  <div
    class="offcanvas offcanvas-end"
    tabindex="-1"
    id="licenseSelector"
    :class="{ show: showingCart }"
  >
    <div class="offcanvas-header">
      <h5 class="offcanvas-title">
        Buy License for {{ count }} Song{{ count > 1 ? "s" : "" }}
      </h5>
      <button
        type="button"
        class="btn-close text-reset"
        @click="cartStore.hideCart"
      ></button>
    </div>
    <div class="offcanvas-body">
      <div
        v-for="track in tracks"
        :key="track.id"
        class="d-flex justify-content-between align-items-center"
      >
        <div class="cover-container" @click="selectTrack(track)">
          <img
            class="rounded"
            :src="track.thumbnailUrl"
            width="50"
            height="50"
          />
          <svg
            class="play-icon"
            viewBox="0 0 50 50"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="25" cy="25" r="25" fill="rgba(0, 0, 0, 0.6)" />
            <polygon
              points="20,15 35,25 20,35"
              fill="#fff"
              stroke="#fff"
              stroke-width="5"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <div class="d-flex p-2 flex-column flex-grow-1">
          <span class="text-decoration-underline text-nowrap text-uppercase">
            {{ track.artistName }}
          </span>
          {{ track.title }}
        </div>
        <fa
          :icon="['far', 'circle-xmark']"
          class="text-secondary"
          @click="cartStore.remove(track)"
          role="button"
        ></fa>
      </div>
      <div v-if="isEmpty">Your cart is empty.</div>
      <div v-else>
        <h5 class="mt-4">What license do you need?</h5>
        <div v-for="question in questions" class="mt-3" :key="question">
          <div v-if="question.answer" class="card">
            <div class="card-body">
              <span
                v-if="question.answer.included"
                class="float-end text-muted fst-italic"
                >included</span
              >
              <span
                v-else
                @click="clearAnswer(question)"
                class="float-end btn btn-link btn-sm"
                >change</span
              >
              {{ question.answer.answer }}
            </div>
          </div>
          <div v-else>
            <div>
              {{ question.title }}
            </div>
            <div
              v-for="choice in question.choices"
              @click="selectAnswer(question, choice)"
              class="btn btn-outline-primary m-2"
              style="min-width: 6em"
              :key="choice"
            >
              {{ choice.label }}
            </div>
            <div v-if="question.multiple_choices" class="d-grid gap-2">
              <div class="mt-2">
                <div
                  v-for="multiple_choice in question.multiple_choices"
                  class="form-check"
                  :key="multiple_choice"
                >
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :value="multiple_choice.name"
                    :id="multiple_choice.name"
                    v-model="selectedDistributions"
                  />
                  <label class="form-check-label" :for="multiple_choice.name">
                    {{ multiple_choice.label }}
                  </label>
                </div>
              </div>
              <div
                class="btn btn-primary"
                @click="selectDistribution(question)"
              >
                Continue
              </div>
            </div>
          </div>
        </div>
        <div v-if="showLicense" class="mt-3 d-grid">
          <fa icon="arrow-down" class="fa-3x mx-auto" />
          <div class="card mt-3">
            <div class="card-body row">
              <div class="col-12 d-grid mt-3">
                <div class="fs-2 fw-bold">
                  {{ requiredLicense.name }} LICENSE
                </div>
                <div
                  v-if="requiredLicense.key !== 'custom'"
                  class="fs-3 fw-bold text-end"
                >
                  {{ requiredLicense.price }} / song
                </div>
                <template v-if="requiredLicense.contactUs">
                  <p class="mt-3">
                    Please click the button below to send us an email with your
                    full name, business email, end-client name, and additional
                    details (term of use, etc...):
                  </p>
                  <a class="btn btn-primary btn-lg" role="button" :href="mailto"
                    >Get a Quote
                  </a>
                </template>
                <template v-else>
                  <p class="mt-3">
                    Please click the button below to send us an email, we will
                    reply shortly with a custom payment link.
                  </p>
                  <a class="btn btn-primary btn-lg" role="button" :href="mailto"
                    >Buy Now
                  </a>
                  <div
                    class="mt-4 d-flex justify-content-between align-items-center"
                  >
                    <fa :icon="['fab', 'cc-apple-pay']" class="fa-2x" />
                    <fa :icon="['fab', 'cc-visa']" class="fa-2x" />
                    <fa :icon="['fab', 'cc-mastercard']" class="fa-2x" />
                    <fa :icon="['fab', 'cc-amex']" class="fa-2x" />
                    <fa :icon="['fab', 'cc-discover']" class="fa-2x" />
                    <fa :icon="['fab', 'cc-jcb']" class="fa-2x" />
                    <fa :icon="['fab', 'cc-diners-club']" class="fa-2x" />
                  </div>
                  <p class="mt-2">and many more (20+ payment methods)</p>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="showingCart"
    @click="cartStore.hideCart"
    class="offcanvas-backdrop fade show"
  ></div>
</template>
